import {Circle, Fill, Icon, Stroke, Style, Text} from 'ol/style.js';
import type {Color} from 'ol/color.js';
import {toString, fromString} from 'ol/color.js';
import Point from 'ol/geom/Point.js';
import Feature from 'ol/Feature.js';
import type RenderFeature from 'ol/render/Feature';
import {LineString} from 'ol/geom.js';
import {edittrackMode$} from '../../stores/store-map.ts';
import {getArrowIconForRotation, lineStringToArrows} from './arrowStyle.ts';
import {distance, type Coordinate} from 'ol/coordinate.js';
import {tourStyleData$} from '../../stores/store-tours.ts';
import type VectorLayer from 'ol/layer/Vector.js';
import type VectorSource from 'ol/source/Vector.js';
import type {StyleFunction} from 'ol/style/Style';
import type CircleStyle from 'ol/style/Circle';

let tourColor = [55, 97, 164];
let opacity = 1;
let edit = false;
const print = false;

const pointFocusRed = [173, 9, 29];
const pointLightFocusRed = [...pointFocusRed, 0.3];

const geometryAScratch = new Point([0, 0]);

export const poiSvgSketch = `
<svg xmlns="http://www.w3.org/2000/svg" width="36" height="60">
  <path fill="${toString(pointFocusRed)}" stroke="${toString(
    pointLightFocusRed,
  )}" stroke-width="16" paint-oder="stroke" d="M18 8.177c-5 0-9.5 3-9.5 9 0 4 9.5 24 9.5 24s9.5-20 9.5-24c0-6-4.5-9-9.5-9Z"/>
  <path fill="${toString(
    pointFocusRed,
  )}" paint-oder="stroke" d="M18 8.177c-5 0-9.5 3-9.5 9 0 4 9.5 24 9.5 24s9.5-20 9.5-24c0-6-4.5-9-9.5-9Z"/>
</svg>
`;

const poiSvgSketchHit = `
<svg xmlns="http://www.w3.org/2000/svg" width="36" height="60">
  <path fill="${toString(pointFocusRed)}" stroke="${toString(
    pointLightFocusRed,
  )}" stroke-width="16" paint-oder="stroke" d="M18 8.177c-5 0-9.5 3-9.5 9 0 4 9.5 24 9.5 24s9.5-20 9.5-24c0-6-4.5-9-9.5-9Z"/>
  <path fill="${toString(
    pointFocusRed,
  )}" stroke="white" stroke-width="2" paint-oder="stroke" d="M18 8.177c-5 0-9.5 3-9.5 9 0 4 9.5 24 9.5 24s9.5-20 9.5-24c0-6-4.5-9-9.5-9Z"/>
</svg>
`;

const poiSvg = (color: number[] | string, opacity: number): string => {
  const fill = typeof color === 'string' ? color : toRgbString(color);
  return `<svg viewBox="4.995 6.624 25.509 36.721" width="26px" height="37px" xmlns="http://www.w3.org/2000/svg">
  <path fill="${fill}" fill-opacity="${opacity}" stroke="white" stroke-width="2" paint-oder="stroke"
  d="M18 8.177c-5 0-9.5 3-9.5 9 0 4 9.5 24 9.5 24s9.5-20 9.5-24c0-6-4.5-9-9.5-9Z"
  transform="matrix(1, 0, 0, 1, 5.684341886080802e-14, 2.842170943040401e-14)"/>
</svg>
`;
};

const withPointerDevice = window.matchMedia('(pointer: coarse)').matches;

/**
 * rgba does not work in MFP 3.29
 */
export function toRgbString(color: Color): string {
  let r = color[0];
  if (r !== (r | 0)) {
    r = (r + 0.5) | 0;
  }
  let g = color[1];
  if (g !== (g | 0)) {
    g = (g + 0.5) | 0;
  }
  let b = color[2];
  if (b !== (b | 0)) {
    b = (b + 0.5) | 0;
  }
  return 'rgb(' + r + ',' + g + ',' + b + ')';
}

const directionArrowSvgA = (color: number[] | string, opacity: number) => {
  const fill = typeof color === 'string' ? color : toRgbString(color);
  return `<svg width="12" height="17" viewBox="0 0 12 17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3.54 0L0 3.54L2.6 6.14V6.13H3.98L1.38 3.54L3.5 1.41L10.22 8.13L3.5 14.85L1.38 12.73L3.98 10.13H2.61L0 12.73L3.54 16.27L11.67 8.14L3.54 0Z" fill="white"/>
    <path d="M1.38 12.73L3.5 14.85L10.22 8.13L3.5 1.41L1.38 3.54L3.98 6.13L5.97 8.14L3.98 10.13L1.38 12.73Z" fill="${fill}" fill-opacity="${opacity}"/>
  </svg>
  `;
};
const directionArrowIconSizeA = [19, 27];

edittrackMode$.subscribe((mode) => {
  edit = mode && mode === 'edit';
});

tourStyleData$.subscribe((styleData) => {
  tourColor = fromString(styleData.colorRgb).slice(0, 3);
  opacity = styleData.opacity / 100;
});

export function controlPointStyle({
  color = [...tourColor, opacity],
  radius = 8,
  text = undefined,
}: {
  color?: number[];
  radius?: number;
  text?: string;
} = {}) {
  const controlPoint = new Style({
    zIndex: 100,
    image: new Circle({
      fill: new Fill({
        color,
      }),
      stroke: new Stroke({
        width: 2,
        color: '#fff',
      }),
      radius,
    }),
    text: new Text({
      font: 'bold 11px Inter',
      fill: new Fill({
        color: '#fff',
      }),
      text,
    }),
  });
  return controlPoint;
}

export function pointStyle({
  color = [...tourColor, opacity],
  radius = 8,
  text = undefined,
}: {
  color?: number[];
  radius?: number;
  text?: string;
} = {}) {
  radius = print ? 6 : edit ? 8 : 10;

  return [
    new Style({
      zIndex: 0,
      image: new Circle({
        radius: edit ? 0 : 12,
        fill: new Fill({
          color: [255, 255, 255, 1],
        }),
      }),
    }),
    new Style({
      zIndex: 1,
      image: new Circle({
        radius: radius,
        fill: new Fill({
          color,
        }),
        stroke: edit
          ? new Stroke({
              width: 2,
              color: '#fff',
            })
          : undefined,
      }),
      text: new Text({
        text: text,
        offsetY: 1,
        font: 'bold 10px sans-serif',
        fill: new Fill({
          color:
            tourColor === fromString('#ffe500').slice(0, 3)
              ? [0, 0, 0, 1]
              : [255, 255, 255, 1],
        }),
      }),
    }),
  ];
}

export function sketchControlPointStyle({
  stroke = undefined,
  radius = 16,
  color = pointFocusRed,
}: {
  stroke?: Stroke | undefined;
  radius?: number;
  color?: number[];
} = {}) {
  const sketchControlPoint = [
    new Style({
      zIndex: 200,
      image: new Circle({
        fill: new Fill({
          color: pointLightFocusRed,
        }),
        radius,
      }),
    }),
    new Style({
      zIndex: 200,
      image: new Circle({
        fill: new Fill({
          color,
        }),
        stroke,
        radius: 8,
      }),
    }),
  ];
  return sketchControlPoint;
}

export const controlPointSketchHit = controlPointStyle({color: pointFocusRed});

// mouse over a control point or dragging a new control point on a segment
const stroke = new Stroke({
  width: 2,
  color: '#fff',
});
export const sketchControlPointHint = sketchControlPointStyle({stroke});
export const defaultSketchControlPoint = sketchControlPointStyle();
export const profileHover = sketchControlPointStyle({
  radius: 6,
  color: [0, 0, 0, 0.3],
});

export function poiPoint(color: number[]): Style {
  return new Style({
    image: new Icon({
      src: `data:image/svg+xml;base64,${btoa(poiSvg(tourColor, opacity))}`,
      anchorOrigin: 'bottom-left',
      anchorXUnits: 'fraction',
      anchorYUnits: 'fraction',
      anchor: [0.5, 0],
    }),
    text: new Text({
      font: 'bold 11px Inter Variable',
      offsetY: -25,
      fill: new Fill({
        color: '#fff',
      }),
    }),
  });
}

export const poiPointSketchHit = new Style({
  zIndex: 200,
  image: new Icon({
    src: `data:image/svg+xml;base64,${btoa(poiSvgSketchHit)}`,
    offset: [0, 0],
    anchorOrigin: 'bottom-left',
    anchorXUnits: 'fraction',
    anchorYUnits: 'pixels',
    anchor: [0.5, 16],
  }),
  text: new Text({
    font: '11px Inter Variable',
    offsetY: -25,
    fill: new Fill({
      color: '#fff',
    }),
  }),
});

export function trackLineStyle({
  color = [...tourColor, opacity],
  width = 6,
}: {
  color?: number[];
  width?: number;
} = {}) {
  const trackLine = new Style({
    stroke: new Stroke({
      color: color,
      width: width,
    }),
  });
  return trackLine;
}

export function trackOutlineStyle({
  color = [255, 255, 255, opacity],
  width = 10,
}: {
  color?: number[];
  width?: number;
} = {}) {
  const trackOutline = new Style({
    stroke: new Stroke({
      color: color,
      width: width,
    }),
    zIndex: -10,
  });
  return trackOutline;
}

export function createArrowAlongLineStyle(
  resolution: number,
  lineString: LineString,
): Style[] {
  let arrowIntervalPx;
  if (resolution <= 20) {
    arrowIntervalPx = 300;
  } else if (resolution <= 50) {
    arrowIntervalPx = 250;
  } else if (resolution <= 200) {
    arrowIntervalPx = 150;
  } else {
    arrowIntervalPx = 75;
  }
  return lineStringToArrows(
    lineString,
    resolution * arrowIntervalPx,
    (position: Coordinate, rotation: number) => {
      return new Style({
        geometry: new Point(position),
        image: getArrowIconForRotation(rotation, {
          key: `da-${tourColor.join('-')}-${opacity}`,
          path: `data:image/svg+xml;base64,${btoa(directionArrowSvgA(tourColor, opacity))}`,
          width: directionArrowIconSizeA[0],
          height: directionArrowIconSizeA[1],
        }),
      });
    },
  );
}

function displaceStyle(
  source: VectorSource,
  point: Feature<Point>,
  styles: Style[],
  resolution: number,
) {
  if (edit) {
    return;
  }
  const initialPointCoordinate = point.getGeometry().getCoordinates();
  const featuresAtCoordinate = source.getFeaturesAtCoordinate(
    initialPointCoordinate,
  );
  // We use the arrowLine because otherwise we would have to find the first and last segment
  const lineFeature = featuresAtCoordinate.find(
    (f) => f.get('type') === 'arrowLine',
  );
  if (!lineFeature) {
    return;
  }

  const lineGeom = lineFeature.getGeometry() as LineString;
  const A = lineGeom.getFirstCoordinate();
  const B = lineGeom.getLastCoordinate();
  const startEndDistance = distance(A, B);
  const distanceInPixels = startEndDistance / resolution;
  const radius = (styles[0].getImage() as CircleStyle).getRadius();
  if (distanceInPixels <= radius) {
    let direction = 0;
    switch (point.get('subtype')) {
      case 'first':
        direction = -1;
        break;
      case 'last':
        direction = 1;
        break;
    }
    // A B circles should overlap and the 2 letters appear very close to each other
    styles.forEach((s) => {
      s.getImage()?.setDisplacement([(direction * radius) / 2, 0]);
      s.getText()?.setOffsetX((direction * radius * 2) / 3);
    });
    if (direction === 1) {
      // ensure that the B point is horizontally aligned with the A point
      geometryAScratch.setCoordinates(A);
      styles.forEach((s) => s.setGeometry(geometryAScratch));
    }
  }
}

export function createEdittrackStyleFunction(
  source: VectorSource,
): StyleFunction {
  // In order to be able to offset the A and B points, we need access to the source.
  return (feature: Feature | RenderFeature, resolution: number) =>
    edittrackStyleFunction(source, feature, resolution);
}

function edittrackStyleFunction(
  source: VectorSource,
  feature: Feature | RenderFeature,
  resolution: number,
): Style | Style[] | void {
  const type = feature.get('type');
  const subtype = feature.get('subtype');
  const sketchHitGeometry = feature.get('sketchHitGeometry');
  const trackLine = trackLineStyle();
  const trackOutline = trackOutlineStyle();
  const defaultControlPoint = controlPointStyle();
  const segmentIntermediatePoint = controlPointStyle({radius: 4});
  const firstControlPoint = pointStyle({text: 'A'});
  const lastControlPoint = pointStyle({text: 'B'});
  const pauseControlPoint = pointStyle({text: 'II'});
  const trackLineModifying = trackLineStyle();
  trackLineModifying.getStroke().setLineDash([1, 12]);
  trackLineModifying.getStroke().setColor([...tourColor, 0.6]);

  switch (type) {
    case 'arrowLine': {
      const mergedLineString = <LineString>feature.getGeometry();
      // only display arrows / line is invisible
      const arrowStyles = createArrowAlongLineStyle(
        resolution,
        mergedLineString,
      );
      return arrowStyles;
    }
    case 'sketch':
      if (!withPointerDevice && subtype) {
        return null;
        // sketchLabel.getText().setText(sketchLabelText[subtype]);
        // return sketchLabel;
      }
      return withPointerDevice ? null : defaultSketchControlPoint;
    case 'POI': {
      if (sketchHitGeometry) {
        return poiPointSketchHit;
      }
      const poi =
        subtype === 'selected' ? poiPoint(pointFocusRed) : poiPoint(tourColor);
      const index = feature.getProperties()['index'];
      if (index !== undefined) {
        poi.getText().setText((index + 1).toString());
      }
      return poi;
    }
    case 'controlPoint':
      if (!withPointerDevice && sketchHitGeometry) {
        return sketchControlPointHint;
      }
      switch (subtype) {
        case 'first':
          displaceStyle(
            source,
            feature as Feature<Point>,
            firstControlPoint,
            resolution,
          );
          return firstControlPoint;
        case 'last':
          displaceStyle(
            source,
            feature as Feature<Point>,
            lastControlPoint,
            resolution,
          );
          return lastControlPoint;
        case 'pause':
          return pauseControlPoint;
        default:
          return edit ? defaultControlPoint : undefined;
      }
    case 'segment':
      switch (subtype) {
        case 'modifying':
          return trackLineModifying;
        default: {
          const styles = [trackOutline, trackLine];
          if (edit) {
            const intermediatePoint = segmentIntermediatePoint.clone();
            intermediatePoint.setGeometry(
              new Point(
                (feature.getGeometry() as LineString).getFlatMidpoint(),
              ),
            );
            styles.push(intermediatePoint);
          }
          if (!withPointerDevice && sketchHitGeometry) {
            const dragging = feature.get('dragging');
            const pointStyle = (
              dragging ? sketchControlPointHint : defaultSketchControlPoint
            ).map((style) => style.clone());
            pointStyle.forEach((style) => style.setGeometry(sketchHitGeometry));
            styles.push(...pointStyle);
          }
          return styles;
        }
      }
    default:
      // console.assert(false, "unknown feature type");
      return undefined;
  }
}

export function updateMergedLineString(
  layer: VectorLayer<VectorSource>,
  segments: readonly Feature<LineString>[],
) {
  const coos = [];
  for (const s of segments) {
    coos.push(...s.getGeometry().getCoordinates());
  }
  let ml = layer
    .getSource()
    .getFeatureById('mergedLine') as Feature<LineString>;
  if (!ml) {
    ml = new Feature({
      type: 'arrowLine',
      geometry: new LineString(coos),
    });
    ml.setId('mergedLine');
    layer.getSource().addFeature(ml);
  } else {
    ml.getGeometry().setCoordinates(coos);
  }
}
